import { REVALIDATE_TIME } from "@/constants/cache"
import { getLocalStorageData } from "@/(admin)/modules/auth/services/redux/auth/login.slice"
import { BATCH_PATH } from "@/constants/path-api"
import httpClient from "@/lib/http-client"
import { bannersApi } from "@/services/banner"
import type { BatchDataMap } from "./batch-data-context"
import type { BatchRequestItem } from "./extract-batch-requests"


type BatchResponseEntry =
  | {
      data?: object[]
      meta?: object
    }
  | {
      error?: string
    }

type BatchResponse = Record<string, BatchResponseEntry>

type NextRequestInit = RequestInit & {
  next?: {
    revalidate: number
  }
}

export const fetchBatchData = async (requests: BatchRequestItem[]): Promise<BatchDataMap> => {
  
  if (requests.length === 0) {
    return {}
  }

  const token = await getLocalStorageData("auth_token")
  const hasAuth = !!token
  const revalidateTime = hasAuth ? false : REVALIDATE_TIME

  // Tách requests thành 2 nhóm: batch (posts/youtube-videos) và banners
  const batchRequests = requests.filter(r => r.type !== "banners")
  const bannerRequests = requests.filter(r => r.type === "banners")
  

  const result: BatchDataMap = {}

  try {
    // 1️⃣ Fetch batch requests (posts, youtube-videos) từ API batch
    if (batchRequests.length > 0) {
      const response = await httpClient.request<BatchResponse>("POST", {
        url: BATCH_PATH,
        data: { requests: batchRequests },
        otherOptions: {
          next: {
            revalidate: revalidateTime,
          },
        } as NextRequestInit,
      })

      if (response) {
        Object.assign(result, response)
      } else {
        console.warn("⚠️ [fetchBatchData] No batch response")
      }
    }

    // 2️⃣ Fetch banners riêng - Gọi bannersApi trực tiếp
    for (const bannerRequest of bannerRequests) {
      try {
        // Extract banner IDs từ filters format: { id: { $in: ["4", "29"] } }
        const bannerIds = (bannerRequest.filters?.id as any)?.$in || []
        
        if (!bannerIds.length) {
          result[bannerRequest.key] = {
            data: [],
            meta: undefined,
          }
          continue
        }
        // Build query cho bannersApi.getBanners
        // API format: filters[id][$in][0]=4&filters[id][$in][1]=29
        const query: Record<string, any> = {
          populate: "*",
          sort: "createdAt:desc",
          publicationState: "preview",
          locale: "all",
          pagination: {
            pageSize: 20,
          },
        }
        
        // Thêm filter IDs theo Strapi format
        bannerIds.forEach((id: string, index: number) => {
          query[`filters[id][$containsi][${index}]`] = id
        })
        // Gọi bannersApi.getBanners
        const bannerResponse = await bannersApi.getBanners(query, {
          next: { revalidate: revalidateTime }
        })
        const banners = bannerResponse.data?.data || []
        result[bannerRequest.key] = {
          data: banners,
          meta: bannerResponse.data?.meta,
        }
      } catch (error) {
        console.error(`❌ [fetchBatchData] Failed to fetch banners for key ${bannerRequest.key}:`, error)
        result[bannerRequest.key] = {
          error: "Failed to fetch banners",
          data: [],
        }
      }
    }

    return result
  } catch (error) {
    console.error("❌ [fetchBatchData] Failed to fetch batch data:", error)
    return {}
  }
}